import { useCallback, useEffect, useRef } from "react";
import { useFetcher } from "react-router";

import type { BuilderContent } from "@builder.io/sdk-react";

import type { PageRef } from "~/hooks/learning-center/transformers";
import type {
  LmsContentType,
  loader,
} from "~/routes/resources/learning-center-content";

// Client fetch hooks for the LMS Builder content tree (SFCC-11503). Each wraps
// the `/resources/learning-center-content` resource route via `useFetcher`
// (server-side `swrFetchOneEntry` + enrich). Ports composable's
// useFetchGoal/Module/ParentLevel/ParentGoal/ModulePageUrl. Resource routes are
// absolute + not locale-segmented, so no `useURL` (which would also throw if a
// caller renders above SiteContext — see the nav-tracker gotcha).

const RESOURCE_URL = "/resources/learning-center-content";

interface LmsFetchResult {
  data: BuilderContent | null;
  isLoading: boolean;
  error: string | null;
  /** Force a refetch of this entry (the resource never auto-revalidates). */
  reload: () => void;
}

function useLmsContent(type: LmsContentType, id?: string): LmsFetchResult {
  const fetcher = useFetcher<typeof loader>({
    key: `lms-${type}-${id ?? "none"}`,
  });

  // Stable per fetcher key (which embeds id); recreated only when the target
  // changes. Exposed as `reload` so a caller can FORCE a refetch — the resource
  // sets `shouldRevalidate=()=>false` (SFCC-11733), so this fetcher never
  // auto-revalidates, and an intermittently-partial fetch (e.g. a blank quiz
  // inlined in the module entry) would otherwise stick until remount. (SFCC-11738.)
  //
  // `fetcher` is read through a ref, NOT captured: when `id` arrives after mount
  // (parentLevel/parentGoal/modulePageUrl — their ids come from a prior fetch),
  // `useFetcher` swaps to the new key via a render-phase setState, and the
  // `useCallback` from that render's first pass — whose `fetcher.load` is still
  // bound to the OLD `lms-…-none` key — survives the re-run (same deps). The
  // load then landed under the old key and this hook read `undefined` forever:
  // no level data → no next module → "Next Module" did nothing (COM-588). Nor
  // can `fetcher` go in the deps: it's a new object every render, which made
  // the `[reload]` effect re-fire `load` on every render (an infinite fetch
  // loop, SFCC-11738).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const reload = useCallback(() => {
    if (!id) return;
    fetcherRef.current.load(
      `${RESOURCE_URL}?type=${type}&id=${encodeURIComponent(id)}`,
    );
  }, [type, id]);

  // Initial load on mount / target change.
  useEffect(() => {
    reload();
  }, [reload]);

  return {
    data: fetcher.data?.entry ?? null,
    // SWR `isLoading` semantics, NOT `isValidating`: loading === "the first
    // fetch hasn't returned yet", i.e. `fetcher.data === undefined`. True through
    // the whole idle→loading window — including the pre-paint gap BEFORE the
    // `[type,id]` load effect fires — so the module block shows its loader during
    // that ~2s instead of the "Please select a module" empty branch. False once
    // data exists, INCLUDING on RR7 revalidations: there `fetcher.state` returns
    // to "loading" but `fetcher.data` is RETAINED, so gating on `state` repainted
    // the module skeleton over already-rendered content on any post-action
    // revalidation — and because the skeleton branch unmounts the accordion, its
    // on-mount markOnly seed re-fired, looping skeleton↔content (SFCC-11733). A
    // fresh target id is a new fetcher key (fresh undefined data), so navigation
    // to another module still shows the loader, not the previous module.
    isLoading: !!id && fetcher.data === undefined,
    error: fetcher.data?.error ?? null,
    reload,
  };
}

/** Goal content entry (levels→modules enriched). */
export const useFetchGoal = (goalContentId?: string): LmsFetchResult =>
  useLmsContent("goal", goalContentId);

/** Module content entry (lessons/quiz enriched). */
export const useFetchModule = (moduleContentId?: string): LmsFetchResult =>
  useLmsContent("module", moduleContentId);

/** The level whose modules contain `moduleContentId`. */
export const useFetchParentLevel = (moduleContentId?: string): LmsFetchResult =>
  useLmsContent("parentLevel", moduleContentId);

/** The goal whose levels contain `levelContentId`. */
export const useFetchParentGoal = (levelContentId?: string): LmsFetchResult =>
  useLmsContent("parentGoal", levelContentId);

/** Questionnaire question entry by id (firstQuestion + each next question). */
export const useFetchQuestion = (questionId?: string): LmsFetchResult =>
  useLmsContent("question", questionId);

/** A module's page URL (breadcrumb / next-module nav), by `data.id`. */
export const useFetchModulePageUrl = (
  moduleId?: string,
): LmsFetchResult & { modulePageUrl: string | undefined } => {
  const result = useLmsContent("modulePageUrl", moduleId);
  const modulePageUrl = (result.data?.data as { page?: PageRef } | undefined)
    ?.page?.value?.data?.url;
  return { ...result, modulePageUrl };
};
